export interface Coord {
  lat: number
  lng: number
}

export interface DistrictOption {
  id: string
  code?: string
  nameEn: string
  nameAr: string
  provinceCode?: string
  provinceId?: string | number
  coordinates?: Coord[]
  boundary?: string // POLYGON WKT format for custom districts
}

export interface DistrictApiDto {
  id?: string
  districtId?: string
  code?: string
  value?: string
  /** When present (batch get-by-provinces), used to bucket districts per province */
  provinceCode?: string
  provinceId?: string | number
  nameEn?: string
  nameAr?: string
  districtNameEn?: string
  districtNameAr?: string
  name?: string
  coordinates?: Array<Coord | number[]>
  geometry?: Array<Coord | number[]>
  boundary?: string // POLYGON WKT format: "POLYGON((lng lat, lng lat, ...))"
}

export const normalizeDistricts = (payload: any): DistrictOption[] => {
  const source = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : []

  return source.map((district: DistrictApiDto, index: number) => {
    const derivedId = district?.id ?? district?.districtId ?? district?.value ?? `district_${index}`
    const derivedCode = district?.code ?? district?.id ?? district?.districtId ?? district?.value ?? `district_${index}`

    const englishName =
      district?.nameEn ?? district?.districtNameEn ?? district?.name ?? `District ${index + 1}`

    const arabicName = district?.nameAr ?? district?.districtNameAr ?? district?.name ?? englishName

    const coordinates = extractCoordinates(district)

    return {
      id: String(derivedId),
      code: String(derivedCode),
      nameEn: englishName,
      nameAr: arabicName,
      provinceCode:
        district?.provinceCode !== undefined && district?.provinceCode !== null
          ? String(district.provinceCode)
          : undefined,
      provinceId: district?.provinceId,
      coordinates,
      boundary: district?.boundary, // Preserve boundary field for custom districts
    }
  })
}

// Parse POLYGON WKT format to Coord[]
// Format: POLYGON((lng lat, lng lat, ...))
const parsePolygonWKT = (wktString: string): Coord[] | null => {
  if (!wktString || typeof wktString !== 'string') {
    return null
  }

  // Remove POLYGON( and ) from the string, also handle \r\n
  const cleaned = wktString.trim().replace(/\r\n/g, '').replace(/\n/g, '')
  const match = cleaned.match(/POLYGON\s*\(\s*\((.*?)\)\s*\)/i)
  if (!match || !match[1]) {
    return null
  }

  // Split by comma to get coordinate pairs
  const points = match[1].split(',')
  const coords: Coord[] = []

  for (const point of points) {
    const trimmed = point.trim()
    if (!trimmed) continue

    // Split by space (format: lng lat)
    const parts = trimmed.split(/\s+/)
    if (parts.length >= 2) {
      const lng = parseFloat(parts[0])
      const lat = parseFloat(parts[1])
      if (!isNaN(lng) && !isNaN(lat)) {
        coords.push({ lat, lng })
      }
    }
  }

  // Remove duplicate last point if it's the same as first (closed polygon)
  if (coords.length > 1) {
    const first = coords[0]
    const last = coords[coords.length - 1]
    if (Math.abs(first.lat - last.lat) < 0.000001 && Math.abs(first.lng - last.lng) < 0.000001) {
      coords.pop()
    }
  }

  return coords.length > 0 ? coords : null
}

const extractCoordinates = (district: DistrictApiDto): Coord[] | undefined => {
  // Extract from boundary field first (API format)
  // Boundary is always a POLYGON WKT string: "POLYGON((lng lat, lng lat, ...))"
  if (district?.boundary && typeof district.boundary === 'string') {
    const coords = parsePolygonWKT(district.boundary)
    if (coords && coords.length > 0) {
      return coords
    }
  }

  // Fallback to coordinates field
  if (Array.isArray(district?.coordinates)) {
    return district.coordinates
      .map((point) => formatCoordinatePoint(point))
      .filter((point: Coord | null): point is Coord => Boolean(point))
  }

  // Fallback to geometry field
  if (Array.isArray(district?.geometry)) {
    return district.geometry
      .map((point) => formatCoordinatePoint(point))
      .filter((point: Coord | null): point is Coord => Boolean(point))
  }

  return undefined
}

const formatCoordinatePoint = (point: Coord | number[] | undefined | null): Coord | null => {
  if (!point) {
    return null
  }

  if (typeof point === 'object' && 'lat' in point && 'lng' in point) {
    const maybePoint = point as Coord
    if (typeof maybePoint.lat === 'number' && typeof maybePoint.lng === 'number') {
      return {lat: maybePoint.lat, lng: maybePoint.lng}
    }
  }

  if (Array.isArray(point) && point.length === 2) {
    const [lat, lng] = point
    const latNum = Number(lat)
    const lngNum = Number(lng)
    if (!Number.isNaN(latNum) && !Number.isNaN(lngNum)) {
      return {lat: latNum, lng: lngNum}
    }
  }

  return null
}


